import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { copyToClipboard } from './useMediscribe';
import { resolveScheduledVisitType } from '../utils/defaultSchedule';
import { applyHistoryRegistry } from '../utils/historyFieldRegistry';
import { queueOfflineDraft } from '../utils/offlineStore';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * Hook for form-level actions:
 * - clearAll, saveDraft, nextPatient
 * - restoreFormData, loadHistoryItem, loadNoteFromHistory
 * - selectRole, selectPatientType, switchRole
 * - saveDefaultProvider, copyRestrictions
 */
const useFormActions = ({
  // Notes
  notes, setNotes, saveNotesForUndo,
  // Patient form setters
  setImagingOrders, setSpecialties,
  setPatientAge, setPatientGender, setPatientStatus, setChiefComplaint, setManualIcd10,
  setPrimaryIcd10Codes, setPatientLabel, setPatientName,
  setAppointmentType,
  // Feb 28, 2026 — bug fix: Reason for Appointment + custom reason text
  // were NOT being cleared between encounters, so the prior patient's
  // dropdown selection (and any free-text in the "other" field) silently
  // carried into the next note. Same for appointmentType. Add the setters
  // here and reset them in clearAll().
  setReasonForVisit, setCustomReasonForVisit,
  setLetterType, setLetterContext, setSelectedTemplate,
  setResult, setAutoFilledFields,
  setSoldierRank, setSoldierFirstName, setSoldierLastName, setSoldierDob, setSoldierUnit, setSoldierPhone,
  setOccupation, setJobDuties, setIncludeCoverLetter,
  // ICD-10 reorder
  setManualIcd10Order,
  // Prior-visit context (Feb 25, 2026) — cleared on Next Patient like notes are.
  setPriorVisitContext,
  // Pre-Visit Prep / RN handoff (Mar 3, 2026) — provider reported it was
  // carrying over between encounters. Now cleared alongside prior-visit ctx.
  setPrepNotes,
  // Deferred topics
  deferredTopics, setDeferredTopics,
  // Labs
  setLabOrders, setShowLabsSection, setMedsRxd,
  // Generation toggles
  setGenerateIndication, setGenerateClinicalNote, setGenerateAdminLetter, setGenerateLimitations,
  // Draft / session
  currentDraftId, setCurrentDraftId, setAutoSavedDraft, setDocumentsReady, setGenerationVersion,
  // Recording
  recording,
  // Autosave
  restoreFormData: restoreFormDataFromHook,
  // UI state
  setSelectedHistoryItem, setActiveOutputTab, setLastShareUrl,
  showHistory, loadHistory,
  shareCode, updateShareSession,
  // Form state for draft save
  patientAge, patientGender, patientStatus, chiefComplaint,
  imagingOrders, specialties, manualIcd10, guestId,
  // Role
  setUserRole, setPatientType, setIsRoleSwitching,
  effectiveRole,
  // Provider defaults
  providerSignatureBlock, mtfName, customMtf,
  setProviderSignatureBlock, setMtfName, setCustomMtf,
  // Visit type
  defaultVisitType, setVisitType,
  // Generating state
  isGenerating,
  // Auto-detect reset
  resetAutoDetect,
  // MTF options for checking
  MTF_OPTIONS,
  // Schedule auto-advance
  clinicSchedule, setClinicSchedule,
  // Toggle section resets
  setIncludeROS, setIncludePE, setPeUnremarkable, setPeExceptions,
  setShowImagingSection, setShowReferralsSection, setIncludeEmAnalysis,
  // Signature block DOS override (history restore needs this to stamp original date, not today's)
  setDosOverride,
  // v6.8.48 — Late-entry checkbox is per-encounter; must clear on Next Patient.
  setDosTimeOverride,
  setLateEntry,
  // Bracket fields cache (persists answered date / fields across regenerates; must clear per-patient)
  setBracketFields,
}) => {
  // Restore all form fields from a saved data object
  const restoreFormData = useCallback((data) => {
    if (!data) return;
    // Clear ALL ENCOUNTER fields first to prevent prior patient data from leaking
    // Feb 24, 2026: Identity fields (patientName, soldierFirstName/Last/DOB/
    // Rank/Unit/Phone, occupation, jobDuties, mtfName, providerSignatureBlock)
    // are NO LONGER pre-cleared here — they persist via localStorage. If the
    // restored note has them, they'll be set explicitly below; otherwise the
    // user's persistent identity remains intact.
    setNotes('');
    setPatientAge(''); setPatientGender(''); setPatientStatus('');
    setChiefComplaint(''); setManualIcd10('');
    if (setPrimaryIcd10Codes) setPrimaryIcd10Codes([]);
    if (setManualIcd10Order) setManualIcd10Order(null);
    setImagingOrders([{ type: '', bodyPart: '' }]); setSpecialties(['']);
    setLetterType(''); setLetterContext('');
    if (setDeferredTopics) setDeferredTopics('');
    setResult(null);
    // Now restore incoming data
    if (data.notes) setNotes(data.notes);
    if (data.patientAge !== undefined) setPatientAge(data.patientAge);
    if (data.patientGender !== undefined) setPatientGender(data.patientGender);
    if (data.patientStatus !== undefined) setPatientStatus(data.patientStatus);
    if (data.chiefComplaint !== undefined) setChiefComplaint(data.chiefComplaint);
    if (data.imagingOrders) setImagingOrders(data.imagingOrders);
    if (data.specialties) setSpecialties(data.specialties);
    // Always clear ICD-10 fields when restoring — prevents prior patient's codes from leaking
    setManualIcd10(data.manualIcd10 || '');
    if (setManualIcd10Order) setManualIcd10Order(null);
    if (setPrimaryIcd10Codes) setPrimaryIcd10Codes(data.primaryIcd10Codes || []);
    if (data.charLimit !== undefined) { /* charLimit setter if available */ }
    if (data.letterType !== undefined) setLetterType(data.letterType);
    if (data.letterContext !== undefined) setLetterContext(data.letterContext);
    if (data.soldierRank !== undefined) setSoldierRank(data.soldierRank);
    if (data.soldierFirstName !== undefined) setSoldierFirstName(data.soldierFirstName);
    if (data.soldierLastName !== undefined) setSoldierLastName(data.soldierLastName);
    if (data.soldierDob !== undefined) setSoldierDob(data.soldierDob);
    if (data.soldierUnit !== undefined) setSoldierUnit(data.soldierUnit);
    if (data.soldierPhone !== undefined) setSoldierPhone(data.soldierPhone);
    if (data.mtfName !== undefined) setMtfName(data.mtfName);
    if (data.customMtf !== undefined) setCustomMtf(data.customMtf);
    if (data.providerSignatureBlock !== undefined) setProviderSignatureBlock(data.providerSignatureBlock);
    if (data.encounterType !== undefined) { /* encounterType setter */ }
    if (data.visitType !== undefined) setVisitType(data.visitType);
    if (data.includeROS !== undefined) { /* includeROS setter */ }
    if (data.includePE !== undefined) { /* includePE setter */ }
    // Feb 26, 2026 — When restoring from history, the SAVED `generateClinicalNote`
    // flag was often `false` if the original save was orders-only. Loading that
    // draft and clicking Generate then produced ONLY referrals/imaging, never
    // a clinical note. Force these flags ON when restoring; the provider can
    // uncheck them in the output-options panel if they want orders-only again.
    if (setGenerateClinicalNote) setGenerateClinicalNote(true);
    if (data.generateAdminLetter !== undefined) setGenerateAdminLetter(data.generateAdminLetter);
    if (data.generateLimitations !== undefined) setGenerateLimitations(data.generateLimitations);
    if (data.occupation !== undefined) setOccupation(data.occupation);
    if (data.jobDuties !== undefined) setJobDuties(data.jobDuties);
    if ((data.deferredTopics || data.deferred_topics) !== undefined && setDeferredTopics) setDeferredTopics(data.deferredTopics || data.deferred_topics || '');
    if (data.result) setResult(data.result);
  }, [setNotes, setPatientAge, setPatientGender, setPatientStatus, setChiefComplaint,
      setImagingOrders, setSpecialties, setManualIcd10, setLetterType,
      setLetterContext, setSoldierRank, setSoldierFirstName, setSoldierLastName, setSoldierDob,
      setSoldierUnit, setSoldierPhone, setMtfName, setCustomMtf, setProviderSignatureBlock,
      setVisitType, setGenerateClinicalNote, setGenerateAdminLetter, setGenerateLimitations,
      setOccupation, setJobDuties, setResult, setDeferredTopics]);

  // Clear all form data
  // Autosave is already in place (writeright_autosave + save-draft flow), and the
  // app offers a "Resume where you left off" recovery modal on return. So instead
  // of a blocking OS confirm, we quietly stash an undo snapshot via saveNotesForUndo
  // and proceed. Provider can still recover via the recovery modal or history drafts.
  const clearAll = useCallback((skipConfirm = false) => {
    // Note: skipConfirm retained for API compatibility with existing call sites
    // (nextPatient etc.), but we no longer show a confirm dialog regardless.
    // Feb 24, 2026: Identity fields (patientName, soldierFirstName, soldierRank,
    // unit, phone, dob, occupation, jobDuties) NO LONGER cleared — these are
    // sticky per-user via localStorage. "Next Patient" only clears
    // encounter-specific data (notes, age, gender, chief complaint, labs,
    // imaging, ICD-10, result). User can manually edit identity if needed.
    void skipConfirm;
    if (notes.trim()) saveNotesForUndo(notes);
    setNotes(''); setImagingOrders([]); setSpecialties([]);
    setPatientAge(''); setPatientGender(''); setChiefComplaint(''); setManualIcd10('');
    // Feb 25, 2026 — also clear prior-visit context on Next Patient.
    if (setPriorVisitContext) setPriorVisitContext('');
    // Mar 3, 2026 — Pre-Visit Prep / RN handoff was carrying over between
    // encounters. Provider reported: "previous encounter field notes doesn't
    // clear with next patient." The Prep field is per-encounter (RN report,
    // BP, prep thoughts) — MUST reset so the next visit doesn't inherit it.
    if (setPrepNotes) setPrepNotes('');
    // Feb 25, 2026 — user explicitly asked patient_status (Active Duty / Civilian /
    // Dependent / Veteran) to CLEAR on Next Patient. It still persists on refresh
    // because usePatientForm keeps it in localStorage; we just clear it here so
    // the next patient doesn't inherit it. Identity (name, rank, unit, etc.)
    // remains untouched.
    setPatientStatus('');
    setPrimaryIcd10Codes([]); setPatientLabel(''); setLetterType(''); setLetterContext('');
    setSelectedTemplate(''); setResult(null); setAutoFilledFields({ age: false, gender: false, status: false, chiefComplaint: false });
    setIncludeCoverLetter(false);
    // Feb 28, 2026 — clear Reason for Appointment + custom reason text +
    // appointment type so the prior patient's encounter context does NOT
    // carry into the next note. User reported: "the reason for appointment
    // dropdown keeps carrying over to the next encounter."
    if (setReasonForVisit) setReasonForVisit('other');
    if (setCustomReasonForVisit) setCustomReasonForVisit('');
    if (setAppointmentType) setAppointmentType('');
    if (setDeferredTopics) setDeferredTopics('');
    if (setLabOrders) setLabOrders('');
    if (setShowLabsSection) setShowLabsSection(false);
    if (setMedsRxd) setMedsRxd(false);
    if (setManualIcd10Order) setManualIcd10Order(null);
    if (recording.clearTranscriptionError) recording.clearTranscriptionError();
    localStorage.removeItem('writeright_autosave');
    // Clear CPT selections so codes from the previous patient (e.g. 99214 + 96127
    // auto-added for a F-code BH screening) don't carry over into the next note.
    localStorage.removeItem('writeright_selected_cpts');
    try { window.dispatchEvent(new CustomEvent('writeright:clear-cpts')); } catch { /* ignore */ }
    if (resetAutoDetect) resetAutoDetect();
    setSelectedHistoryItem(null); setCurrentDraftId(null); setAutoSavedDraft(false); setDocumentsReady(false);
    if (setGenerationVersion) { setGenerationVersion(0); localStorage.setItem('writeright_generation_version', '0'); }
    // Reset visit type to user default
    if (setVisitType) setVisitType(defaultVisitType || 'in_person_exam');
    // Reset document section toggles to defaults
    if (setIncludeROS) setIncludeROS(true);
    if (setIncludePE) setIncludePE(true);
    if (setPeUnremarkable) setPeUnremarkable(false);
    if (setPeExceptions) setPeExceptions('');    if (setShowImagingSection) setShowImagingSection(false);
    if (setShowReferralsSection) setShowReferralsSection(false);
    if (setGenerateLimitations) setGenerateLimitations(false);
    if (setGenerateAdminLetter) setGenerateAdminLetter(false);
    if (setIncludeEmAnalysis) setIncludeEmAnalysis(false);
    if (setGenerateClinicalNote) setGenerateClinicalNote(true);
    // Feb 18, 2026: clear cached letter answers + DOS override so the next
    // patient doesn't inherit the previous patient's date.
    if (setBracketFields) setBracketFields({});
    if (setDosOverride) setDosOverride('');
    // v6.8.48 — Reset time of service + late-entry checkbox per-encounter.
    if (setDosTimeOverride) setDosTimeOverride('');
    if (setLateEntry) setLateEntry(false);
    // Mark current schedule slot as done but do NOT auto-select next — let provider choose
    if (clinicSchedule && clinicSchedule.length > 0 && setClinicSchedule) {
      const updated = clinicSchedule.map(s => s.status === 'current' ? { ...s, status: 'done' } : s);
      setClinicSchedule(updated);
      localStorage.setItem('clinicSchedule', JSON.stringify(updated));
      // Clear label so provider picks the next slot manually
      if (setPatientLabel) setPatientLabel('');
    }
    if (showHistory) loadHistory();
    // Re-apply visit type from schedule + derive PE defaults from it
    // (Virtual → PE off; In-person → PE on + Remarkable)
    // Override: "Always Remarkable PE" Profile setting wins regardless of schedule.
    let _peAlwaysRemarkable = false;
    try { _peAlwaysRemarkable = localStorage.getItem('writeright_pe_always_remarkable') === 'true'; } catch { /* ignore */ }
    // Use the shared resolver (seeds localStorage with sensible defaults if
    // the user has never saved Profile Settings).
    const _resolvedVisitType = resolveScheduledVisitType();
    if (setVisitType) setVisitType(_resolvedVisitType);
    const _isVirtual = _resolvedVisitType === 'virtual' || _resolvedVisitType === 'results_review_virtual';
    const _isInPerson = _resolvedVisitType === 'in_person_exam' || _resolvedVisitType === 'results_review';
    if (setIncludePE) setIncludePE(_peAlwaysRemarkable ? true : !_isVirtual);
    // PE default is now REMARKABLE everywhere (Feb 14, 2026 user pref).
    if (setPeUnremarkable) setPeUnremarkable(false);
    toast.success('Cleared!');
  }, [notes, saveNotesForUndo, setNotes, setImagingOrders, setSpecialties, setPatientAge, setPatientGender,
      setPatientStatus, setChiefComplaint, setManualIcd10, setPrimaryIcd10Codes, setPatientLabel,
      setLetterType, setLetterContext, setSelectedTemplate, setResult, setAutoFilledFields,
      setSoldierRank, setSoldierFirstName, setSoldierLastName, setSoldierDob, setSoldierUnit, setSoldierPhone,
      setOccupation, setJobDuties, setIncludeCoverLetter, recording, resetAutoDetect,
      setSelectedHistoryItem, setCurrentDraftId, setAutoSavedDraft, setDocumentsReady, setGenerationVersion,
      setPatientName, showHistory, loadHistory, defaultVisitType, setVisitType,
      // Feb 28, 2026 — added so the next-patient clear actually resets these.
      setReasonForVisit, setCustomReasonForVisit, setAppointmentType,
      setIncludeROS, setIncludePE, setPeUnremarkable, setPeExceptions,
      setShowImagingSection, setShowReferralsSection, setIncludeEmAnalysis,
      setGenerateLimitations, setGenerateAdminLetter, setGenerateClinicalNote,
      clinicSchedule, setClinicSchedule, setDeferredTopics, setManualIcd10Order, setLabOrders, setShowLabsSection,
      setBracketFields, setDosOverride, setPrepNotes]);

  // Save draft
  const saveDraft = useCallback(async () => {
    if (!notes.trim() || notes.trim().length < 10) { toast.error('Need at least a few words to save'); return false; }
    // Build the full state snapshot once — we either POST it or queue it.
    const snapshot = {
      notes,
      patient_age: patientAge ? parseInt(patientAge) : null,
      patient_gender: patientGender || null,
      patient_status: patientStatus || null,
      chief_complaint: chiefComplaint || null,
      imaging_orders: imagingOrders.filter(i => i.type?.trim()) || null,
      specialties: specialties.filter(s => s?.trim()) || null,
      icd10_codes: manualIcd10 || null,
      guest_id: guestId,
      deferred_topics: deferredTopics || null,
      ...(currentDraftId && { draft_id: currentDraftId }),
    };
    // OFFLINE PATH: if the browser tells us we're offline, queue the snapshot
    // into IndexedDB instead of failing. The OfflineQueuePanel + auto-drain
    // hook will replay it once connectivity returns. Feb 16, 2026 — Lean
    // Offline MVP.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      try {
        const id = await queueOfflineDraft({
          ...snapshot,
          patient_label: snapshot.chief_complaint || `${snapshot.patient_age || '?'}yo ${snapshot.patient_gender || ''}`.trim(),
          target_endpoint: currentDraftId ? 'update-draft' : 'save-draft',
        });
        if (id) {
          toast.success('Saved offline — will sync when back online', { duration: 4000 });
          return true;
        }
      } catch (e) {
        console.warn('[saveDraft] offline queue failed:', e);
      }
      toast.error('Offline — could not save draft locally');
      return false;
    }
    try {
      const ep = currentDraftId ? `${API}/history/update-draft` : `${API}/history/save-draft`;
      const res = await axios.post(ep, snapshot, { withCredentials: true });
      if (res.data.success) {
        if (res.data.draft_id && !currentDraftId) setCurrentDraftId(res.data.draft_id);
        toast.success(currentDraftId ? 'Draft updated!' : 'Draft saved!', { duration: 3000 });
        loadHistory();
        if (shareCode && updateShareSession) { updateShareSession(); toast.info('Team members can see your saved draft!', { duration: 2000 }); }
        // Feb 27, 2026 — return the canonical draft id (or `true` if the
        // backend didn't echo one back) so callers like the Re-Baton handler
        // can immediately POST /api/teams/{team_id}/share-note without
        // waiting for the React state update to flush.
        return res.data.draft_id || currentDraftId || true;
      }
    } catch (e) {
      // Network failure even though navigator.onLine claimed online: queue too.
      if (e?.code === 'ERR_NETWORK' || e?.code === 'ECONNABORTED' || !e?.response) {
        try {
          const id = await queueOfflineDraft({
            ...snapshot,
            patient_label: snapshot.chief_complaint || `${snapshot.patient_age || '?'}yo ${snapshot.patient_gender || ''}`.trim(),
            target_endpoint: currentDraftId ? 'update-draft' : 'save-draft',
          });
          if (id) {
            toast.success('Network issue — saved offline. Will retry when stable.', { duration: 4000 });
            return true;
          }
        } catch { /* fall through to error */ }
      }
      toast.error('Failed to save draft');
      return false;
    }
    return false;
  }, [notes, currentDraftId, patientAge, patientGender, patientStatus, chiefComplaint,
      imagingOrders, specialties, manualIcd10, guestId, setCurrentDraftId, loadHistory,
      shareCode, updateShareSession, deferredTopics]);

  // Next patient
  const nextPatient = useCallback(async () => {
    if (recording.isTranscribing) { toast.error('Transcription in progress'); return; }
    // Fire the breath-check bump event before any early-return path so the reminder
    // counts every attempt to move on regardless of what actually happens
    try { window.dispatchEvent(new CustomEvent('writeright:next-patient')); } catch { /* ignore */ }
    if (isGenerating) { toast.info('Generation in progress - opening new tab'); window.open(window.location.href, '_self'); return; }

    // Feb 27, 2026 — User reported that in 1-column layout Next Patient was
    // STILL landing at the bottom (where the output had been) despite the
    // post-clear smooth-scroll. Cause: smooth scrolling races with React's
    // re-render of the cleared output, and the browser preserves the previous
    // scroll position until the smooth animation catches up. Add an
    // IMMEDIATE synchronous instant-scroll to (0,0) BEFORE clearAll so the
    // viewport hits the top before any layout shift can happen.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch { /* no-op */ }

    // After clearAll, smooth-scroll back up to the dictation textarea (where
    // the provider holds the microphone) — fixes the bug reported May 7,
    // 2026 (and again Feb 15, 2026 — Next Patient was landing at the bottom
    // of the left column in two-column view because scrollIntoView only
    // scrolls the nearest scrollable ancestor, not the window itself).
    const scrollToInput = () => {
      try {
        setTimeout(() => {
          // 1) Force the OUTER window to the very top first — covers both
          //    single-column scroll and two-column-with-page-scroll layouts.
          //    Feb 27, 2026: use 'auto' (instant) instead of 'smooth' so the
          //    1-column mobile layout doesn't visibly drift up after landing.
          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          } catch { /* ignore */ }
          // 2) Also scroll any inner scroll containers (the input column in
          //    two-column view often has its own overflow-y).
          try {
            const inputCard = document.querySelector('[data-testid="input-card"]')
              || document.getElementById('input-section');
            if (inputCard && inputCard.scrollTop !== undefined) inputCard.scrollTop = 0;
            const inputColumn = document.querySelector('#input-section');
            if (inputColumn) {
              let parent = inputColumn.parentElement;
              while (parent && parent !== document.body) {
                if (parent.scrollHeight > parent.clientHeight) parent.scrollTop = 0;
                parent = parent.parentElement;
              }
            }
          } catch { /* ignore */ }
          // 3) Refine focus by scrolling the textarea into view (only AFTER
          //    the window-level scroll has landed us at the top).
          setTimeout(() => {
            const target = document.querySelector('[data-testid="notes-textarea"]')
              || document.querySelector('[data-testid="patient-age-input"]')
              || document.querySelector('[data-testid="input-card"]');
            if (target) {
              try {
                // Feb 27, 2026: 'auto' (instant) in 1-col so the viewport
                // doesn't slide down again past the just-pinned top.
                target.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
              } catch { /* ignore */ }
              // Re-pin the window to (0,0) once more after scrollIntoView so
              // any sticky-header offset doesn't leave the input partially
              // hidden below the toolbar.
              try {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
              } catch { /* ignore */ }
              if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
                setTimeout(() => { try { target.focus({ preventScroll: true }); } catch { /* ignore */ } }, 300);
              }
            }
            // Mar 1, 2026 — Final hammer pin AFTER React paints + after focus()
            // resolves. User reported mobile-only regression where Next Patient
            // landed mid-page despite the earlier scroll calls — React's scroll
            // anchor restoration was undoing them. Two chained RAFs guarantee
            // we run AFTER the browser's next paint commits.
            requestAnimationFrame(() => requestAnimationFrame(() => {
              try {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
              } catch { /* ignore */ }
            }));
          }, 220);
        }, 60);
      } catch { /* no-op */ }
    };

    if (notes.trim().length > 10 && !saveDraft) {
      clearAll(true);
      scrollToInput();
      return;
    }
    if (notes.trim().length > 10) {
      toast.loading('Saving draft...', { id: 'next-patient-save' });
      const saved = await saveDraft();
      if (saved) { toast.success('Draft saved! Ready for next patient.', { id: 'next-patient-save', duration: 2000 }); }
      else { toast.error('Could not save draft — clearing anyway.', { id: 'next-patient-save' }); }
      clearAll(true);
      scrollToInput();
      return;
    }
    clearAll(true);
    scrollToInput();
  }, [recording, isGenerating, notes, saveDraft, clearAll]);

  // Load history item
  //
  // ALL simple form fields are restored/cleared via HISTORY_FIELD_REGISTRY
  // (see `utils/historyFieldRegistry.js`). To add a new field that should
  // participate in history reload, add ONE line to that registry — do NOT
  // sprinkle ad-hoc restores here. Feb 16, 2026: this pattern was introduced
  // after two consecutive field-bleed bugs (deferredTopics, peExceptions).
  //
  // Only complex setters that don't fit the simple
  // `setter(item[key] ?? default)` shape (the `result` object, output-tab
  // selection, side effects like clearing CPTs and showing a toast) remain
  // hand-coded below.
  const loadHistoryItem = useCallback((item) => {
    // Apply registry-driven field restores
    applyHistoryRegistry(item, {
      setNotes,
      setPatientAge,
      setPatientGender,
      setPatientStatus,
      setPatientName,
      setChiefComplaint,
      setDeferredTopics,
      setPeExceptions,
      setManualIcd10,
      setGenerateClinicalNote,
      setGenerateAdminLetter,
      setGenerateLimitations,
      setImagingOrders,
      setSpecialties,
    });

    // Feb 27, 2026 — Un-dead Generate after history/team load.
    // The registry's transform for `generateClinicalNote` is `!!item.clinical_note`,
    // which yields FALSE when the user re-opens a notes-only draft that was
    // saved before any clinical note was generated. The Generate button then
    // silently rejects with "Please select at least one output type" because
    // every gate is false. Force the clinical-note gate ON after the registry
    // applies — provider can still uncheck it manually if they truly want
    // orders-only output.
    if (setGenerateClinicalNote) setGenerateClinicalNote(true);
    // Also reset the conflict-scan bypass flag (held inside useGeneration via
    // useRef). A stale `true` from a prior "Generate Anyway" would silently
    // skip scanning the newly-loaded text. Decoupled via window event because
    // useFormActions does not own the useGeneration ref.
    try { window.dispatchEvent(new CustomEvent('writeright:history-loaded')); } catch { /* no-op */ }

    // Feb 18, 2026 bugfix: when a provider restores a past note, the signature
    // block was stamping TODAY's date because dosOverride defaults to ''.
    // Pull the original generation timestamp (created_at or timestamp) and feed
    // it into dosOverride as YYYY-MM-DD so the Date of Service in the
    // signature block reflects when the note was actually signed.
    if (setDosOverride) {
      try {
        const originalISO = item.created_at || item.timestamp || item.generated_at;
        if (originalISO) {
          const d = new Date(originalISO);
          if (!isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            setDosOverride(`${yyyy}-${mm}-${dd}`);
          } else {
            setDosOverride('');
          }
        } else {
          setDosOverride('');
        }
      } catch (e) {
        setDosOverride('');
      }
    }

    // Complex / non-registry setters
    setResult({
      // Feb 22, 2026 — Preserve history_id when restoring a draft. Without
      // this, the EncounterStoplight falls back to a synthetic enc-<len>-<len>
      // id and the backend "completed" sync silently no-ops on every sign
      // action from a loaded draft (the sync guard is `!hid.startsWith('enc-')`).
      // Knock-on: the dashboard "signed today" counter pulled from the server
      // never reflected drafts re-signed from history.
      history_id: item.history_id || item.id,
      id: item.id,
      indication: item.indication,
      indication_char_count: item.indication?.length,
      referrals: item.referrals || null,
      imaging_orders: item.imaging_orders || null,
      clinical_note: item.clinical_note,
      admin_letter: item.admin_letter,
      limitations: item.limitations,
      icd10_suggestions: item.icd10_suggestions || [],
      original_notes: item.original_notes,
    });
    setSelectedHistoryItem(item.id);
    if (item.share_url) setLastShareUrl(item.share_url);
    try { localStorage.removeItem('writeright_selected_cpts'); } catch(e) {}
    if (item.referrals?.length > 1) setActiveOutputTab('referral-0');
    else if (item.limitations) setActiveOutputTab('limitations');
    else if (item.admin_letter) setActiveOutputTab('admin_letter');
    else if (item.clinical_note) setActiveOutputTab('clinical_note');
    else if (item.indication) setActiveOutputTab('indication');
    toast.success(effectiveRole === 'patient' ? 'Loaded from history! Add more details and click "Generate Letter" to update.' : 'Loaded — notes, ICD-10s, and doc types restored', { duration: effectiveRole === 'patient' ? 5000 : 3000 });
  }, [setNotes, setResult, setSelectedHistoryItem, setLastShareUrl, setPatientAge, setPatientGender,
      setPatientName, setPatientStatus, setActiveOutputTab, effectiveRole, setManualIcd10,
      setChiefComplaint, setGenerateClinicalNote, setGenerateAdminLetter, setGenerateLimitations,
      setImagingOrders, setSpecialties, setDeferredTopics, setPeExceptions, setDosOverride]);

  // Load note from history (weekend catchup variant)
  const loadNoteFromHistory = useCallback((entry) => {
    setSelectedHistoryItem(entry.id);
    if (entry.original_notes) setNotes(entry.original_notes);
    if (setManualIcd10) setManualIcd10('');
    try { localStorage.removeItem('writeright_selected_cpts'); } catch(e) {}
    restoreFormData({ patientAge: entry.patient_age, patientGender: entry.patient_gender, patientStatus: entry.patient_status, chiefComplaint: entry.chief_complaint, deferred_topics: entry.deferred_topics });
    if (entry.clinical_note || entry.admin_letter || entry.indication) {
      setResult({ clinical_note: entry.clinical_note, admin_letter: entry.admin_letter, indication: entry.indication, limitations: entry.limitations, referrals: entry.referrals, imaging_orders: entry.imaging_orders, icd10_suggestions: entry.icd10_suggestions || [], proposed_medications: entry.proposed_medications, proposed_profile: entry.proposed_profile });
    }
  }, [setSelectedHistoryItem, setNotes, restoreFormData, setResult, setManualIcd10]);

  // Role selection
  const selectRole = useCallback((role) => {
    setIsRoleSwitching(false);
    setUserRole(role);
    localStorage.setItem('writeright_role', role);
    if (role === 'provider') {
      setGenerateClinicalNote(true);
      setGenerateIndication(true);
    } else {
      setGenerateClinicalNote(false);
      setGenerateIndication(false);
      setGenerateAdminLetter(true);
      setGenerateLimitations(false);
    }
  }, [setIsRoleSwitching, setUserRole, setGenerateClinicalNote, setGenerateIndication, setGenerateAdminLetter, setGenerateLimitations]);

  const selectPatientType = useCallback((type) => {
    setPatientType(type);
    localStorage.setItem('writeright_patient_type', type);
    if (type === 'active_duty') setPatientStatus('active_duty');
    else if (type === 'dependent') setPatientStatus('dependent');
    else setPatientStatus('civilian');
  }, [setPatientType, setPatientStatus]);

  const switchRole = useCallback(async () => {
    // Feb 26, 2026 — switching role MUST clear the clinical input so provider
    // notes don't leak into a patient letter (or vice-versa). If there's
    // unsaved work, auto-save a draft first; toast informs the user it's
    // recoverable from History. Best-effort: never block the role switch
    // on save failure.
    try {
      if (notes && notes.trim().length > 20) {
        await saveDraft();
      }
    } catch (e) { /* never block role switch on save failure */ }
    clearAll(true);
    setIsRoleSwitching(true);
    setUserRole(null);
    setPatientType(null);
    localStorage.removeItem('writeright_role');
    localStorage.removeItem('writeright_patient_type');
  }, [notes, clearAll, saveDraft, setIsRoleSwitching, setUserRole, setPatientType]);

  // Save default provider
  const saveDefaultProvider = useCallback(() => {
    if (providerSignatureBlock) localStorage.setItem('mediscribe_default_provider', providerSignatureBlock);
    const effectiveMtf = mtfName === 'custom' ? customMtf : mtfName;
    if (effectiveMtf) localStorage.setItem('mediscribe_default_mtf', effectiveMtf);
    toast.success('Default provider & MTF saved!');
  }, [providerSignatureBlock, mtfName, customMtf]);

  // Copy restrictions
  const copyRestrictions = useCallback(async (restrictions) => {
    const text = restrictions.join('\n• ');
    const formatted = '• ' + text;
    const success = await copyToClipboard(formatted);
    if (success) toast.success('Restrictions copied! Paste into MEDPROS free text field.');
    else toast.error('Copy failed - check browser permissions');
  }, []);

  return {
    restoreFormData, clearAll, saveDraft, nextPatient,
    loadHistoryItem, loadNoteFromHistory,
    selectRole, selectPatientType, switchRole,
    saveDefaultProvider, copyRestrictions,
  };
};

export default useFormActions;
